import { MetricsKind } from "helpers/metrics/types";
import { ColumnVO } from "ag-grid-community/dist/lib/interfaces/iColumnVO";
import { IServerSideGetRowsRequest } from "ag-grid-community/dist/lib/interfaces/iServerSideDatasource";
import { AggFuncs, ALLOCATION_DIMENSION_ID_PREFIX, Kinds, ValueTypes } from "constants/product-collection";
import { formatMessage } from "lib/globalize-util";
import siteConfig from "site-config";
import { IDoor } from "./door-collection";
import { nullish } from "./nullish";
import {
    getFormattedDateObject,
    getValueFormatter,
    getValueGetter,
    ISchemaKey,
    roundPercentage,
} from "./product-collection";
import { List, Map } from "immutable";
import { RollupSnapshotStatus } from "containers/folders/types/server-rollups";
import { STATUS_TO_RECREATE_SNAPSHOT } from "constants/server-rollup";
import { IAllocationDimension, IAllocationDimensionValue, isAllocationDimensionId } from "./allocation-dimension";
import { SortOrder } from "constants/types";

export const COL_DEF_SEPARATOR = "|";
export const ROLLUP_SIZE_PAGE = 500;
export const ROLLUP_DOOR_METRICS_SIZE_PAGE = 25;
export const HASHTAGS_LIST_ITEMS_SIZE_PAGE = 5;
export const ROLLUP_PIVOT_SIZE_PAGE = 1000;
export const SEARCH_AFTER_TOKEN_KEY = "__default";
export const ASSORTMENT_NAME_DELIMITER = "::";
export const IMAGE_URL_FIELD_NAME = "image_url";
export const ASSORTMENT_FIELD_NAME = "assortment_name";
export const COLLECTION_ID_FIELD_NAME = "collection_id";
export const SERVER_SNAPSHOT_DATE_F0RMAT = "YYYY-MM-DD";
export const DEFAULT_DATE_VALUE = "0000-00-00";
export const AGGREGATE_COLUMN_PREFIX = "__agg_";
export const UNSET_GROUP_KEY = "_unset_group_key";
export const NU_CUSTOM_SORT_PREFIX = "nu_custom_sort_";
export const NU_COLLECTION_NAME_FIELD = "nu_collection_name_field";
export const HASHTAG_AUTO_COMPLETE_FIELD = "hashtag_auto_complete_field";

export interface IServerRollupResponse {
    items: IItemElement[];
    group_by: IGroupBy[];
    search_after: string[];
    door_total: IDoorTotal;
    meta: IMeta;
    size_set: string[];
    review_column_header: IReviewColumnHeader;
}

export interface IReviewColumnHeader {
    allocation_total: number;
    bulk_total: number;
    bulk_by_delivery: IAllocationByDelivery;
    allocation_by_delivery: IAllocationByDelivery;
    allocation_by_size: IAllocationByDelivery;
    allocation_by_size_delivery: IAllocationBySizeDelivery;
}

export interface IAllocationByDelivery {
    data: IAllocationByDeliveryData[];
}

export interface IAllocationByDeliveryData {
    aggregation: IAggregation;
}

export interface IAllocationBySizeDelivery {
    [size: string]: IAllocationByDelivery;
}

export interface IItemElement {
    item: IItem;
}

export interface IItem {
    id: string;
    fields: IField;
    bulks: IAllocation[];
    allocations: IAllocation[];
    collection_id: string;
    allocation_dimension_ids: string[];
    door_group_metrics: IDoorGroupMetric[];
    door_total_metrics: IDoorHeaderMetric[];
}

export type DateValueTpe = {
    day: number;
    month: number;
    year: number;
};

export type StringValue = { string_value: string };
export type NumberValue = { number_value: string };
export type BoolValue = { bool_value: string };
export type DateValue = { date_value: DateValueTpe };

export type FieldValueType = StringValue | NumberValue | BoolValue | DateValue;

export interface IField {
    [key: string]: FieldValueType;
}

export interface IAllocation {
    value_id: string[];
    quantity: string;
    cost: number;
    cost_with_conversion: number;
    retail: number;
    retail_with_conversion: number;
}

export interface IDoorTotal {
    doors: IDoorItem[];
}

export interface IDoorItem {
    delivery: string;
    door: string;
    units: number;
    skus: number;
    cost: number;
    cost_with_conversion: number;
    retail: number;
    retail_with_conversion: number;
    sku_total: number;
}

export interface IGroupBy {
    aggregation: IAggregation;
}

export interface IAggregation {
    group_key: FieldValueType;
    values: IAggregatedValues;
    item_count: number;

    group_metrics?: IDoorGroupMetric[];
    total_metrics?: IDoorHeaderMetric[];
}

export interface IAggregatedValues {
    total_quantity: { double_value: number };
    [key: string]: { double_value: number } | { string_value: string };
}

export interface IDoorGroupMetric {
    metric_kind: MetricsKind;
    delivery_id?: string;

    value?: number[]; // Only returned by be
}

export interface IDoorHeaderMetric {
    metric_kind: MetricsKind;
    delivery_id?: string;

    value?: number;
}

export interface IDoorGroupMetricName {
    name: string;
    doors?: string[];
}

export type IAttributeDoorsFilter = {
    [field: string]: string[];
};

export interface IDoorMetricsTotal {
    group_metrics: IDoorGroupMetric[];
    total_metrics: IDoorHeaderMetric[];
}

export interface IMeta {
    pivot_columns: any[];
    total: ITotal;
    door_metrics_total: IDoorMetricsTotal;
}

export interface ITotal {
    units: number;
    products: number;
    rows: number;
    costs: number;
    retail: number;
    sku_total: number;
}

interface IColumnDimensionValue {
    c: string[];
}
interface IRowDimensionValue {
    r: IColumnDimensionValue[];
}
export interface ISnapshotItem {
    fields: IField;
    bulks: IRowDimensionValue;
    allocations: IRowDimensionValue;
    id?: string;
    rowIndex?: number;
    selected_sizes?: string[];

    // Door Metrics:
    doorMetrics?: { [key: string]: number };
    totalMetrics?: { [key: string]: number };

    // calculated fields
    groupId?: string;
    startPath?: string[];
}

export enum AggFuncTypes {
    SUM = "SUM",
    COUNT = "COUNT",
    AVG = "AVG",
    SUM_WITH_PERCENTAGE = "SUM_WITH_PERCENTAGE",
    COUNT_WITH_PERCENTAGE = "COUNT_WITH_PERCENTAGE",
}

export const AggregationFuncGridToServerMap = {
    [AggFuncs.SUM.toUpperCase()]: AggFuncTypes.SUM,
    [AggFuncs.COUNT.toUpperCase()]: AggFuncTypes.COUNT,
    [AggFuncs.AVG.toUpperCase()]: AggFuncTypes.AVG,
    [AggFuncs.SUM_WITH_PERCENT.toUpperCase()]: AggFuncTypes.SUM_WITH_PERCENTAGE,
    [AggFuncs.COUNT_WITH_PERCENT.toUpperCase()]: AggFuncTypes.COUNT_WITH_PERCENTAGE,
};

const DOOR_GROUP_AND_SELECTIONS_SEPARATOR = "===";
export const DOOR_METRICS_GROUP_NAME_SEPARATOR = "|";
export const DOOR_DIMENSION_METRICS_GROUP_NAME = "DOOR";

export const buildDoorGroupNameSelected = (doorGroup: string, classification: string) =>
    `${doorGroup}${DOOR_GROUP_AND_SELECTIONS_SEPARATOR}${classification}`;

export const buildServerRollupRowNodeId = (itemId: string, collectionId: string) => `${itemId}#${collectionId}`;

export const shouldRecreateSnapshot = (status: RollupSnapshotStatus) => STATUS_TO_RECREATE_SNAPSHOT.includes(status);

export const getBlankTextValue = (useNull?: boolean) => (useNull ? null : formatMessage("(Blanks)")); // is we pass null aggrid uses the blank text and this works with all types //formatMessage("(Blanks)");

export const isFieldWithCustomSorting = (schemaField: Map<string, any>) =>
    schemaField?.getIn(["constraint", "allowed_values"])?.size > 0;

export const isBlankValue = (value: string, valutType: ValueTypes) => {
    switch (valutType) {
        case ValueTypes.BOOL:
        case ValueTypes.NUMBER:
        case ValueTypes.DATE:
            return value === UNSET_GROUP_KEY;
        case ValueTypes.STRING:
        default:
            return value === "";
    }
};

export const getServerRollupEndpoint = (rollupSnapshotId: string) =>
    `${siteConfig.goAPI}/v1/collections/rollup_snapshots/${rollupSnapshotId}/read`;

export const getFieldNamesFromValueType = (
    itemDisplayProperty: List<Map<string, any>>,
    valueType: ValueTypes
): string[] => {
    if (!itemDisplayProperty) {
        return [];
    }
    const schemas = itemDisplayProperty.filter((schemaKey) => schemaKey.get("value_type") === valueType);
    return schemas.size ? schemas.map((schema) => schema.getIn(["field_name"])).toArray() : [];
};

export const parseStringIntoDate = (value: string): DateValueTpe => {
    if (!value || value === DEFAULT_DATE_VALUE) {
        return null;
    }

    if (value === UNSET_GROUP_KEY) {
        return value as any;
    }

    const [year, month, day] = value.split("-");
    return {
        day: +day,
        month: +month,
        year: +year,
    };
};

export const mapDateFields = (
    dateFields: string[],
    data: IField,
    valueCols: ColumnVO[] = []
): { [key: string]: DateValue } =>
    dateFields.reduce((acc, key) => {
        const isAggregated = valueCols?.some((it) => it.field === key);
        const value = !isAggregated && parseStringIntoDate((data[key] as StringValue)?.string_value);
        value && (acc[key] = { date_value: value });
        return acc;
    }, {});

export const mapCollectionFieldValue = (
    valueType: ValueTypes,
    value: any,
    dateAsString: boolean = false
): FieldValueType => {
    if (value === "" && dateAsString) {
        return { string_value: UNSET_GROUP_KEY };
    }

    if (value === UNSET_GROUP_KEY) {
        return { string_value: value };
    }

    switch (valueType) {
        case ValueTypes.WITH_PERCENT:
            return {
                number_value: `${nullish(value) ? "" : value}`,
            };
        case ValueTypes.BOOL:
            return {
                bool_value: value,
            };
        case ValueTypes.NUMBER:
            return {
                number_value: value,
            };
        case ValueTypes.DATE:
        case ValueTypes.STRING:
        default: {
            if (!dateAsString && valueType === ValueTypes.DATE) {
                return { date_value: value };
            }
            return {
                string_value: `${nullish(value) ? "" : value}`,
            };
        }
    }
};

export const getGroupKeyValue = (valueType: ValueTypes, groupKey: FieldValueType) => {
    switch (valueType) {
        case ValueTypes.BOOL:
            return (groupKey as BoolValue)?.bool_value;
        case ValueTypes.NUMBER:
            return (groupKey as NumberValue)?.number_value;
        case ValueTypes.STRING:
        default:
            return (groupKey as StringValue)?.string_value;
    }
};

export const getGroupByColumn = (gridParams: IServerSideGetRowsRequest): ColumnVO => {
    return gridParams.rowGroupCols?.length && gridParams.rowGroupCols.length !== gridParams.groupKeys?.length
        ? gridParams.rowGroupCols[gridParams.groupKeys?.length || 0]
        : null;
};

export const isPivotEnabled = (gridParams: IServerSideGetRowsRequest): boolean => {
    return gridParams.pivotMode && gridParams.pivotCols.length > 0 && gridParams.valueCols.length > 0;
};

export const enhanceSecondaryColDefs = (
    pivotColums,
    valueCols: ColumnVO[],
    pivotCols: ColumnVO[],
    valueColsSchema: { [key: string]: ISchemaKey },
    defaultDateFormat: string,
    collectionsRefs?: any,
    childrenLevel?: number
) =>
    pivotColums.map((pivot) => {
        if (pivot.children.length) {
            const column = pivotCols[childrenLevel || 0];
            const schemaField = valueColsSchema[column?.field];
            let headerName = (collectionsRefs && collectionsRefs.get(pivot.header_name)) || pivot.header_name;
            if (schemaField && schemaField.value_type === ValueTypes.DATE) {
                const dateValue = parseStringIntoDate(headerName);
                headerName = getFormattedDateObject(dateValue, defaultDateFormat) || getBlankTextValue();
            }
            return {
                groupId: pivot.group_id,
                headerName: headerName,
                children: enhanceSecondaryColDefs(
                    pivot.children,
                    valueCols,
                    pivotCols,
                    valueColsSchema,
                    defaultDateFormat,
                    collectionsRefs,
                    (childrenLevel || 0) + 1
                ),
            };
        } else {
            let isCollectionName = false;
            if (collectionsRefs) {
                const fields = pivot.column_id.split(COL_DEF_SEPARATOR);
                isCollectionName = fields[fields.length - 1] === COLLECTION_ID_FIELD_NAME;
            }

            const colId = isCollectionName
                ? pivot.column_id.replace(COLLECTION_ID_FIELD_NAME, ASSORTMENT_FIELD_NAME)
                : pivot.column_id;
            const headerName = isCollectionName
                ? pivot.header_name.replace(COLLECTION_ID_FIELD_NAME, ASSORTMENT_FIELD_NAME)
                : pivot.header_name;
            const { field, aggFunc, displayName } =
                valueCols.find(
                    (it) => `${AggregationFuncGridToServerMap[it.aggFunc.toUpperCase()]}(${it.field})` === headerName
                ) || {};
            const schemaKey = valueColsSchema[field] && {
                ...valueColsSchema[field],
                kind: Kinds.DEFAULT_KIND,
                value_type:
                    valueColsSchema[field].value_type === ValueTypes.NUMBER &&
                    [AggFuncs.COUNT_WITH_PERCENT, AggFuncs.SUM_WITH_PERCENT].includes(aggFunc)
                        ? ValueTypes.STRING
                        : valueColsSchema[field].value_type,
            };
            return {
                colId: colId,
                headerName: `${aggFunc}(${displayName})`,
                field: colId,
                valueGetter: schemaKey && getValueGetter(schemaKey),
                valueFormatter: schemaKey && getValueFormatter(schemaKey),
            };
        }
    });

export const createColsHash = (colDefs) => {
    if (!colDefs) {
        return null;
    }
    const parts = [];
    colDefs.forEach((colDef) => {
        if (colDef.children) {
            parts.push(colDef.groupId);
            parts.push("[" + createColsHash(colDef.children) + "]");
        } else {
            parts.push(colDef.colId);
            // headerName can change if the aggFunc was changed in a value col. if we didn't
            // do this, then the grid would not pick up on new header names as we move from
            // eg min to max.
            if (colDef.headerName) {
                parts.push(colDef.headerName);
            }
        }
    });
    return parts.join(",");
};

export const readDoorTotals = (
    doors: IDoor[],
    response: IServerRollupResponse,
    shouldShowTotalsFiltered: boolean = false
) => {
    const totals = doors.map((door) => {
        const doorTotals = (response?.door_total?.doors || []).filter((it) => it.door === door.id);
        const deliveries = doorTotals.map((it) => ({
            key: it.delivery,
            units: it.units,
            skus: it.skus, // in total worker this is an object of [productId]: true,
            retail: it.retail,
            wholesale: it.cost,
            retailWithConversion: it.retail_with_conversion,
            wholesaleWithConversion: it.cost_with_conversion,
        }));

        return {
            key: door.id,
            classifications: door.classifications,
            deliveries: deliveries.reduce((acc, { key, ...props }) => ({ ...acc, [key]: props }), {}),
            units: deliveries.reduce((acc, { units }) => acc + units, 0),
            skus: deliveries.reduce((acc, { skus }) => acc + skus, 0),
            retail: deliveries.reduce((acc, { retail }) => acc + retail, 0),
            wholesale: deliveries.reduce((acc, { wholesale }) => acc + wholesale, 0),
        };
    });

    let doorTotals = {
        units: response?.meta?.total?.units || 0,
        skus: response?.meta?.total?.sku_total || 0,
        retail: response?.meta?.total?.retail || 0,
        wholesale: response?.meta?.total?.costs || 0,
        totalRows: response?.meta?.total?.rows || 0,
        totalProducts: response?.meta?.total?.products || 0,
    };
    if (shouldShowTotalsFiltered) {
        doorTotals = response?.door_total?.doors.reduce(
            (previousValue: any, door: IDoorItem) => {
                return {
                    units: door.units + previousValue.units,
                    skus: Math.max(door.skus, previousValue.skus),
                    retail: door.retail + previousValue.retail,
                    wholesale: door.cost + previousValue.wholesale,
                    totalRows: response?.meta?.total?.rows || 0,
                    totalProducts: response?.meta?.total?.products || 0,
                };
            },
            { units: 0, skus: 0, retail: 0, wholesale: 0, totalRows: 0, totalProducts: 0 }
        );
    }

    return {
        ...totals.reduce((acc, { key, ...props }) => ({ ...acc, [key]: props }), {}),
        _total: {
            deliveries: (response.door_total?.doors || []).reduce((acc, item) => {
                const deliveryId = item.delivery;

                if (acc.hasOwnProperty(deliveryId)) {
                    acc[deliveryId].units += item.units;
                    acc[deliveryId].retail += item.retail_with_conversion;
                    acc[deliveryId].wholesale += item.cost_with_conversion;
                } else {
                    acc[deliveryId] = {
                        units: item.units,
                        skus: item.skus,
                        retail: item.retail_with_conversion,
                        wholesale: item.cost_with_conversion,
                    };
                }

                return acc;
            }, {}),
            ...doorTotals,
        },
    };
};

export const readDoorMetrics = (
    doorMetricsTotal: IDoorMetricsTotal,
    doorGroups: IDoorGroupMetricName[],
    hasDeliveries: boolean
) => {
    const doorMetrics = doorMetricsTotal?.group_metrics?.reduce((acc, groupMetric) => {
        const doorGroupFields = (doorGroups || []).reduce((iacc, dg, index) => {
            iacc[dg.name] = groupMetric.value[index];
            return iacc;
        }, {});

        acc[groupMetric.metric_kind] = {
            ...(acc[groupMetric.metric_kind] || {}),
            ...(hasDeliveries ? { [groupMetric.delivery_id]: doorGroupFields } : doorGroupFields),
        };

        return acc;
    }, {});

    const totalMetrics = doorMetricsTotal?.total_metrics?.reduce((acc, totalMetric) => {
        acc[totalMetric.metric_kind] = hasDeliveries
            ? {
                  ...(acc[totalMetric.metric_kind] || {}),
                  [totalMetric.delivery_id]: totalMetric.value,
              }
            : totalMetric.value;

        return acc;
    }, {});

    return {
        doorMetrics,
        totalMetrics,
    };
};

export const readRows = (
    response: IServerRollupResponse,
    startRow: number,
    collectionsRefs,
    itemDisplayProperty: List<Map<string, any>>,
    addCalculatedFields?: () => {},
    valueCols?: ColumnVO[],
    doorGroups?: IDoorGroupMetricName[],
    hasDeliveries: boolean = false
): ISnapshotItem[] => {
    const dateFields = getFieldNamesFromValueType(itemDisplayProperty, ValueTypes.DATE);

    return response.items.map(({ item }, rowIndex) => {
        const { allocations, bulks, door_group_metrics, door_total_metrics, ...restItem } = item;
        return {
            ...restItem,
            ...(addCalculatedFields?.() || {}),
            ...readDoorMetrics(
                {
                    group_metrics: door_group_metrics,
                    total_metrics: door_total_metrics,
                },
                doorGroups,
                hasDeliveries
            ),
            fields: {
                ...restItem.fields,
                ...mapDateFields(dateFields, restItem.fields, valueCols),
                [ASSORTMENT_FIELD_NAME]: {
                    string_value: `${collectionsRefs.get(item.collection_id)}${ASSORTMENT_NAME_DELIMITER}${
                        item.collection_id
                    }`,
                },
            },
            allocations: {
                r: allocations.map((it) => {
                    return {
                        c: it.value_id.concat([it.quantity]),
                    };
                }),
            },
            bulks: {
                r: bulks.map((it) => {
                    return {
                        c: it.value_id.concat([it.quantity]),
                    };
                }),
            },
            rowIndex: startRow + rowIndex,
        };
    });
};

const readGroupFields = (
    aggregation: IAggregation,
    valueCols: ColumnVO[],
    itemDisplayProperty: List<Map<string, any>>,
    allocationDimensionGroups: boolean = false
) => {
    return valueCols.reduce((acc, value) => {
        const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === value.field);
        const isBulkFormula =
            schemaField && schemaField.get("kind") === Kinds.FORMULA && schemaField.get("rowwise_evaluation");
        let aggFuncKey = AggregationFuncGridToServerMap[value.aggFunc?.toUpperCase()];
        const fieldKey = value.field === ASSORTMENT_FIELD_NAME ? COLLECTION_ID_FIELD_NAME : value.field;
        if (aggFuncKey === AggFuncTypes.SUM_WITH_PERCENTAGE && !isBulkFormula && allocationDimensionGroups) {
            aggFuncKey = AggFuncTypes.SUM;
        }
        const valueGroup = aggregation.values[`${aggFuncKey}(${fieldKey})`];

        switch (aggFuncKey) {
            case AggFuncTypes.SUM_WITH_PERCENTAGE:
            case AggFuncTypes.COUNT_WITH_PERCENTAGE:
                acc[`${AGGREGATE_COLUMN_PREFIX}${value.field}`] = mapCollectionFieldValue(
                    ValueTypes.WITH_PERCENT,
                    (valueGroup as any)?.string_value
                );
                break;
            case AggFuncTypes.AVG:
            case AggFuncTypes.SUM:
            case AggFuncTypes.COUNT:
            default:
                acc[`${AGGREGATE_COLUMN_PREFIX}${value.field}`] = mapCollectionFieldValue(
                    ValueTypes.NUMBER,
                    (valueGroup as any)?.double_value
                );
                break;
        }
        return acc;
    }, {});
};

export const readPivotFields = (aggregation: IAggregation, valueCols: ColumnVO[]) => {
    const aggregatedByCollectionName = valueCols.some((pivot) => pivot.field === ASSORTMENT_FIELD_NAME);
    return Object.keys(aggregation.values).reduce((pacc, pkey) => {
        const theField = aggregation.values[pkey];
        const isDouble = theField.hasOwnProperty("double_value");
        const theValue = isDouble ? (theField as any).double_value : (theField as any).string_value;
        const theKey = aggregatedByCollectionName
            ? pkey.replace(COLLECTION_ID_FIELD_NAME, ASSORTMENT_FIELD_NAME)
            : pkey;
        pacc[theKey] = mapCollectionFieldValue(isDouble ? ValueTypes.NUMBER : ValueTypes.STRING, theValue);
        return pacc;
    }, {});
};

export const readGroupedRows = (
    allocationDimensions: IAllocationDimension[],
    response: IServerRollupResponse,
    groupBy: ColumnVO,
    { rowGroupCols, groupKeys, valueCols }: IServerSideGetRowsRequest,
    itemDisplayProperty: List<Map<string, any>>,
    pivotActive: boolean,
    collectionsRefs?: any,
    doorGroups?: IDoorGroupMetricName[],
    hasDeliveries: boolean = false
) => {
    const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === groupBy?.field);
    const hasGroupValues = rowGroupCols.length && rowGroupCols?.length !== groupKeys?.length;
    const blankTextValue = getBlankTextValue();
    const isCustomSort = schemaField && isFieldWithCustomSorting(schemaField);
    const allocationDimensionGroups = rowGroupCols.filter((groupItem) => isAllocationDimensionId(groupItem.id));

    return response.group_by.map(({ aggregation }) => {
        const groupFields =
            hasGroupValues && !pivotActive
                ? readGroupFields(aggregation, valueCols, itemDisplayProperty, allocationDimensionGroups.length > 0)
                : {};

        const pivotFields = pivotActive ? readPivotFields(aggregation, valueCols) : {};

        const valueType = isCustomSort ? ValueTypes.NUMBER : schemaField?.get("value_type") || ValueTypes.STRING;
        let groupKeyValue = getGroupKeyValue(valueType, aggregation.group_key);

        if (isAllocationDimensionId(groupBy.id)) {
            const currentAllocationDimension = allocationDimensions.find(
                (item) => item.id === groupBy?.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, "")
            );

            if (currentAllocationDimension) {
                const allocationDimensionValueIndex = getGroupKeyValue(ValueTypes.NUMBER, aggregation.group_key);
                const currentAllocationDimensionValue: IAllocationDimensionValue =
                    currentAllocationDimension.dimension_values[allocationDimensionValueIndex];
                groupKeyValue = groupKeyValue === undefined ? currentAllocationDimensionValue?.id : groupKeyValue; // this is for UNSET_GROUP_KEY
            }
        } else {
            groupKeyValue =
                groupKeyValue === undefined
                    ? getGroupKeyValue(ValueTypes.STRING, aggregation.group_key)
                    : groupKeyValue; // this is for UNSET_GROUP_KEY
        }

        let theValue = groupKeyValue === "" ? blankTextValue : groupKeyValue;
        theValue =
            groupBy?.field === ASSORTMENT_FIELD_NAME
                ? `${collectionsRefs.get(groupKeyValue)}${ASSORTMENT_NAME_DELIMITER}${groupKeyValue}`
                : theValue;
        theValue = isCustomSort
            ? schemaField.getIn(["constraint", "allowed_values"]).get(theValue)?.get("string_value") || blankTextValue
            : theValue;
        theValue = valueType === ValueTypes.DATE ? parseStringIntoDate(theValue) : theValue;
        return {
            itemCount: aggregation.item_count ? +aggregation.item_count : 0,
            ...readDoorMetrics(
                {
                    group_metrics: aggregation.group_metrics,
                    total_metrics: aggregation.total_metrics,
                },
                doorGroups,
                hasDeliveries
            ),
            fields: {
                ...groupFields,
                ...pivotFields,
                ...(groupBy && {
                    [groupBy.field]: mapCollectionFieldValue(
                        schemaField?.get("value_type") || ValueTypes.STRING,
                        theValue
                    ),
                }),
            },
        };
    });
};

export const findAllocationDimensionById = (allocationDimensions: IAllocationDimension[], id: string) => {
    return allocationDimensions.find((item) => item.id === id);
};

export const sortRowsByDimensionValueIds = (rows: any, sortedDimensionValueIds: string[], colId: string) => {
    return [...rows].sort((a, b) => {
        const firstItem = a.fields[colId].string_value;
        const secondItem = b.fields[colId].string_value;

        return sortedDimensionValueIds.indexOf(firstItem) - sortedDimensionValueIds.indexOf(secondItem);
    });
};

export const sortAllocationDimensionsGroupedRows = (
    sortGroup: { colId?: string; sort?: SortOrder },
    groupedRows: any[],
    allocationDimensions
) => {
    const currentAllocationDimension = findAllocationDimensionById(allocationDimensions, sortGroup?.colId);

    if (!currentAllocationDimension) {
        return groupedRows;
    }

    const sortedDimensionValueIds = currentAllocationDimension.dimension_values.map(
        (dimensionValue) => dimensionValue.id
    );

    if (sortGroup.sort === SortOrder.DESC) {
        sortedDimensionValueIds.reverse();
    }

    const hasUnsetGroupKey =
        groupedRows[groupedRows.length - 1].fields[sortGroup?.colId]?.string_value === UNSET_GROUP_KEY;

    let emptyRowGroup;

    if (hasUnsetGroupKey) {
        emptyRowGroup = groupedRows.pop();
    }

    // Sort rows based on the order of the sortedDimensionValueIds
    let sortedGroupedRows = sortRowsByDimensionValueIds(groupedRows, sortedDimensionValueIds, sortGroup?.colId);

    if (emptyRowGroup) {
        sortedGroupedRows =
            sortGroup.sort === SortOrder.DESC
                ? [...sortedGroupedRows, emptyRowGroup]
                : [emptyRowGroup, ...sortedGroupedRows];
    }

    return sortedGroupedRows;
};

export const getOnlyDoorGroupedRows = (rows = [], doorsFilter = [], groupBy) => {
    if (doorsFilter.length > 0) {
        return rows.filter((row) => doorsFilter.includes(row.fields[groupBy.id].string_value));
    }

    return rows;
};
const calculateCountTotalsWithMultiplePivotCols = (row, column, rows) => {
    return Object.entries(row.fields)
        .filter(([key, _field]: [string, any]) => key.split(COL_DEF_SEPARATOR).reverse()[0] === column.id)
        .reduce((acc, [key, field]: [string, any]) => {
            const currentValue = field.number_value;
            const allTotalValues = rows.reduce((acc, rowItem) => {
                const fieldKeyFound = Object.keys(rowItem.fields).find((rowItemKey) => rowItemKey === key);

                if (fieldKeyFound) {
                    acc += rowItem.fields[fieldKeyFound].number_value;
                }

                return acc;
            }, 0);
            const valuePercentage = allTotalValues > 0 ? roundPercentage((currentValue / allTotalValues) * 100) : 0;
            acc[key] = { number_value: `${currentValue} (${valuePercentage}%)` };

            return acc;
        }, {});
};

export const updateTotalCalculationsInRows = (
    rows,
    valueCols: ColumnVO[],
    groupsBy,
    itemDisplayProperty,
    hasAllocationDimensionGroup,
    pivotCols: ColumnVO[]
) => {
    const initialRows = [...rows];
    const initialColumns = [...valueCols];

    // Iterate per row and column
    const updatedRows = initialRows.map((row) => {
        let updatedRowFields = { ...row.fields };
        initialColumns.forEach((column) => {
            const aggFunc = column.aggFunc;
            const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === column.field);
            const isBulkFormula = schemaField?.get("rowwise_evaluation");

            if (aggFunc === AggFuncs.COUNT_WITH_PERCENT && hasAllocationDimensionGroup) {
                if (pivotCols.length === 0) {
                    const allTotalValues = rows.reduce((acc, row) => (acc = acc + row.itemCount), 0);
                    const currentValue = row.itemCount;
                    const valuePercentage =
                        allTotalValues > 0 ? roundPercentage((currentValue / allTotalValues) * 100) : 0;

                    updatedRowFields[`__agg_${column.id}`] = {
                        number_value: `${currentValue} (${valuePercentage}%)`,
                    };
                } else {
                    const updatedFields = calculateCountTotalsWithMultiplePivotCols(row, column, rows);
                    updatedRowFields = {
                        ...updatedRowFields,
                        ...updatedFields,
                    };
                }
            } else if (aggFunc === AggFuncs.SUM_WITH_PERCENT && !isBulkFormula && hasAllocationDimensionGroup) {
                if (pivotCols.length === 0) {
                    const allTotalValues = groupsBy.reduce((acc, group) => {
                        const currentField: any = group.aggregation.values[`${AggFuncTypes.SUM}(${column.id})`];
                        const currentGroupSum = currentField?.double_value ?? 0;

                        return acc + currentGroupSum;
                    }, 0);
                    const currentValue = row.fields[`__agg_${column.id}`].number_value;
                    const valuePercentage =
                        allTotalValues > 0 ? roundPercentage((currentValue / allTotalValues) * 100) : 0;

                    updatedRowFields[`__agg_${column.id}`] = {
                        number_value: `${currentValue} (${valuePercentage}%)`,
                    };
                } else {
                    const updatedFields = calculateCountTotalsWithMultiplePivotCols(row, column, rows);
                    updatedRowFields = {
                        ...updatedRowFields,
                        ...updatedFields,
                    };
                }
            }
        });

        return {
            ...row,
            itemCount: row.itemCount,
            fields: updatedRowFields,
        };
    });

    return updatedRows;
};
